// Voice input for the Generate panel — Phase 5 polish.
//
// Wraps the browser's Web Speech API (webkitSpeechRecognition / standard
// SpeechRecognition) so the operator can dictate restyle / inpaint /
// regen prompts in pt-BR instead of typing. Zero-cost: runs entirely in
// the browser, no Vercel function, no API key.
//
// ─── Upgrade path → OpenAI Whisper (or equivalent) ─────────────────
// If Web Speech quality on specific material vocabulary ("nogueira",
// "boiserie", "âmbar quente") turns out insufficient in real demos,
// this module's public surface (`isSupported`, `listen`, `cancel`) is
// vendor-neutral by design — swap the internals without changing any
// former call site was the (now removed) generate panel.
//
// Concrete steps for the Whisper upgrade:
//
//   1. Add ENV: `vercel env add OPENAI_API_KEY production` + redeploy.
//      (Reuses the same provider style as ANTHROPIC_API_KEY for prompt
//      translation. Cost: $0.006/min — ~$0.0001 per 5s utterance.)
//
//   2. Add a Vercel function `api/voice/transcribe.ts`:
//      - POST multipart/form-data with the audio blob.
//      - Forward to `https://api.openai.com/v1/audio/transcriptions`
//        with `model=whisper-1` + `language=pt`.
//      - Return `{ text: string }`.
//      - Rate-limit at 20 transcriptions per 5 min per IP (mirrors
//        the segment endpoint).
//
//   3. Replace `listen()` here with a MediaRecorder flow:
//      - Acquire mic: `navigator.mediaDevices.getUserMedia({ audio: true })`
//      - Start `new MediaRecorder(stream, { mimeType: 'audio/webm' })`
//      - Stop on silence (RMS-based VAD on a 100ms window) OR on second
//        click.
//      - POST the resulting Blob to /api/voice/transcribe.
//      - Resolve with the returned text.
//
//   4. Keep `isSupported()` returning true on all browsers that support
//      `MediaRecorder` + `getUserMedia` (basically everything except
//      old iOS Safari).
//
// Trade-offs vs. current Web Speech:
//   - Pros: SOTA pt-BR accuracy on technical vocabulary. Works on Firefox.
//     Works offline-ish (still needs network to hit OpenAI).
//   - Cons: ~$0.0001 per utterance. ~300-700ms extra latency vs. on-device.
//     Requires the OPENAI_API_KEY env var to be configured (without it,
//     fall back to Web Speech).
//
// Hybrid pattern (recommended if upgrading): try Web Speech first when
// supported, fall back to Whisper if it returns null or fires onerror
// twice in a row. Cheapest path for high quality.

interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResults {
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResults;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  abort: () => void;
}
interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export class VoiceInput {
  private rec: SpeechRecognitionLike | null = null;
  private currentResolve: ((text: string | null) => void) | null = null;

  /** Browser supports the Web Speech API? Chrome / Edge / Safari iOS 14.5+ yes; Firefox no. */
  static isSupported(): boolean {
    return getCtor() !== null;
  }

  /**
   * Start a single-utterance recognition session in the given language
   * (default pt-BR). Resolves with the transcribed text, or `null` if the
   * session ended without a recognized result (silence, user aborted,
   * mic permission denied, browser unsupported).
   *
   * Only one session at a time — calling listen() while a previous one is
   * active aborts the previous and resolves it with null.
   */
  async listen(lang = 'pt-BR'): Promise<string | null> {
    const Ctor = getCtor();
    if (!Ctor) return null;
    this.cancel();
    return new Promise((resolve) => {
      const rec = new Ctor();
      rec.lang = lang;
      rec.continuous = false;
      rec.interimResults = false;
      rec.maxAlternatives = 1;

      this.rec = rec;
      this.currentResolve = resolve;

      let resolved = false;
      const settle = (text: string | null): void => {
        if (resolved) return;
        resolved = true;
        if (this.rec === rec) {
          this.rec = null;
          this.currentResolve = null;
        }
        resolve(text);
      };

      rec.onresult = (e) => {
        const result = e.results[0];
        const alt = result?.[0];
        settle(alt?.transcript?.trim() || null);
      };
      rec.onerror = (e) => {
        console.warn('[voice] recognition error', e);
        settle(null);
      };
      rec.onend = () => {
        // Browsers fire onend after onresult — only treat it as "no result"
        // if onresult didn't already settle.
        settle(null);
      };

      try {
        rec.start();
      } catch (err) {
        console.warn('[voice] rec.start failed', err);
        settle(null);
      }
    });
  }

  /** Abort any in-flight recognition session. Idempotent. */
  cancel(): void {
    if (this.rec) {
      try {
        this.rec.abort();
      } catch {
        /* ignore */
      }
    }
    if (this.currentResolve) {
      const r = this.currentResolve;
      this.rec = null;
      this.currentResolve = null;
      r(null);
    }
  }
}
